import { useRef } from 'react'
import { FileImage } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/shared/components/ui/card'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import {
  useOrgProofsForBrandSettings,
  useUploadOrgProofForBrandSettings,
} from 'src/api/db/apiProof'
import { useToast } from 'src/shared/hooks/use-toast'
import { ProofList } from 'src/pages/_myAccount/Requests/Request/components/tabs/VerifyProofs/components/ProofList'
import { ProofUploadButton } from 'src/pages/_myAccount/Requests/Request/components/tabs/VerifyProofs/components/ProofUploadButton'

export const ProofsSections = () => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const org_id = useGetOrgId()
  const { rows, isLoading } = useOrgProofsForBrandSettings({ org_id })
  const uploadProof = useUploadOrgProofForBrandSettings({ org_id })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const proofsForList = rows.map((r) => r.proof)

  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return

    try {
      await uploadProof.mutateAsync(files)
      toast({
        title:
          files.length === 1
            ? t('Proof uploaded', 'Korrektur uppladdad')
            : t('Proofs uploaded', 'Korrekturer uppladdade'),
      })
    } catch (err) {
      console.error('Brand settings proof upload error:', err)
      toast({
        variant: 'destructive',
        title: t('Upload failed', 'Uppladdning misslyckades'),
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  const isUploadBusy = uploadProof.isPending

  return (
    <Card>
      <CardHeader className="pb-4">
        <Row className="items-start justify-between gap-3">
          <Row className="min-w-0 flex-1 items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <FileImage className="size-5 text-muted-foreground" />
            </div>
            <Column className="min-w-0 flex-1 gap-1">
              <CardTitle>{t('Proofs', 'Korrekturer')}</CardTitle>
              <CardDescription>
                {t(
                  'All factory proofs, newest first.',
                  'Alla fabrikskorrekturer, nyast först.'
                )}
              </CardDescription>
            </Column>
          </Row>
          {org_id ? (
            <Column className="shrink-0">
              <ProofUploadButton
                inputRef={fileInputRef}
                disabled={isLoading || isUploadBusy}
                onFilesSelected={handleUpload}
              />
            </Column>
          ) : null}
        </Row>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <span className="text-sm text-muted-foreground">
            {t('Loading…', 'Laddar…')}
          </span>
        ) : rows.length === 0 ? (
          <Column className="items-center gap-2 rounded-lg border border-dashed border-border bg-muted/15 px-4 py-7">
            <div className="flex size-11 items-center justify-center rounded-full bg-muted/80">
              <FileImage className="size-5 text-muted-foreground" />
            </div>
            <Column className="items-center gap-1">
              <span className="text-center text-sm font-medium">
                {t('No proofs yet', 'Inga korrekturer ännu')}
              </span>
              <span className="max-w-[280px] text-center text-xs text-muted-foreground">
                {t(
                  'Upload PDFs or images (you can select several), or proofs from your orders will show here.',
                  'Ladda upp PDF:er eller bilder (du kan välja flera), eller visa korrektur från dina ordrar här.'
                )}
              </span>
            </Column>
          </Column>
        ) : (
          <ProofList
            proofs={proofsForList}
            readOnly
            preserveOrder
            showStatus={false}
            getRowKey={(_, i) => rows[i]!.rowKey}
          />
        )}
      </CardContent>
    </Card>
  )
}

ProofsSections.displayName = 'ProofsSections'
